// src/components/tiptap/marks.ts
// FieldNode — atomic inline node for a Tab-navigable, clinically meaningful
// chip in the report. ProseMirror treats the chip as one unit (cursor cannot
// enter it; Backspace adjacent deletes the whole chip); the ChipView NodeView
// renders an inner contentEditable span so word-level edits stay in place.
//
// Text lives on `attrs.text`. The inner span writes through to attrs on
// `onInput`, so there is exactly one source of truth; the InlineChipCard
// subscribes via a manual `editor.on("update")` listener (a single React
// root reads from a child-root's attrs).

import { Node, mergeAttributes, type CommandProps } from "@tiptap/core";
import { type Node as ProseMirrorNode } from "@tiptap/pm/model";
import { NodeSelection, Plugin, PluginKey } from "@tiptap/pm/state";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { getDefaultStore } from "jotai";
import { reportFocusAtom, getFocusedChipId } from "../../atoms/reporterChipAtoms";
import type { FindingJumpTarget } from "../../hooks/useReportQuery";
import { ChipView } from "./ChipView";
import { findFieldNodeById, resolveSectionKindAt } from "./extensions";

// Class on the inner contentEditable span that ChipView renders inside each
// chip. Used as the discriminator for edit-mode arbitration in four places:
//   - ChipView's onInput/onBlur handlers (intra-chip text editing)
//   - InlineChipCard.focusInnerSpan (move caret into a chip's text)
//   - FieldNode.addCommands.navigate (focus the next chip's inner span)
//   - PrototypeReport's focused-chip keyboard handler + focusin listener
//   - the Backspace shortcut in this file (skip whole-chip delete if the
//     rad's DOM focus is the inner span — they're deleting a character)
// A typo or rename anywhere silently breaks edit-mode arbitration; export the
// single string so every callsite imports it.
export const CHIP_INNER_CLASS = "text-inner";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    field: {
      /** Focus the next field's inner span and select all its text. Fires
       *  the viewer's onJump callback if the target chip has a linked slice. */
      nextField: () => ReturnType;
      /** Inverse of nextField. */
      prevField: () => ReturnType;
      /** Promote an AI suggestion (from the strip card surface or any other
       *  out-of-doc source) into the organSubsection with `attrs.kind ===
       *  section` — `section` is the kind slug ("liver"), the canonical
       *  anchor used everywhere (buildReportDoc, resolveSectionKindAt,
       *  reportFocusAtom), NOT the user-editable display label ("LIVER").
       *  If the subsection holds a single template chip (the
       *  default state for fresh strip/off arms), it is replaced; otherwise
       *  the new chip is appended at the end of the section's paragraph,
       *  separated from the previous chip by a single space. */
      acceptIntoSection: (args: {
        section: string;
        fieldAttrs: Partial<FieldNodeAttrs>;
      }) => ReturnType;
      /** Replace the text of the chip with the given id. Atomic single-tr
       *  update on attrs.text — does NOT mutate PM structure. Used by
       *  dictation's hard-target path (reportFocusAtom mode chip-focused /
       *  caret-in-chip) to overwrite an existing chip without re-routing
       *  via the LLM classifier. Returns false (chain ends, no dispatch)
       *  if no field node with that id exists in the doc. */
      replaceChipText: (chipId: string, text: string) => ReturnType;
    };
  }
  // Tiptap 3 tightened `editor.storage` typing — declare each extension's
  // storage shape here so consumers can read attrs without `as unknown` casts.
  interface Storage {
    field: {
      onJump: ((target: FindingJumpTarget) => void) | null;
    };
  }
}

export type FieldKind = "ai-finding" | "template" | "rad-added" | null;
export type FieldConfidence = "confident" | "maybe" | null;

export interface FieldNodeAttrs {
  /** JSON-encoded source identity and original image references. Wording edits
   * do not change origin or imply that the source verifies the edited text. */
  source: string | null;
  /** Stable identifier for this chip. UUID generated at creation (or
   * carried from the backend's MergeField.id when AI-derived). Lets
   * impression items cite findings by id. */
  id: string | null;
  seriesId: string | null;
  sliceIndex: number | null;
  /** FrameOfReferenceUID of the series this chip points into. Carried on
   *  the chip so the DOM-keyed jump path can resolve a full
   *  FindingJumpTarget without re-querying studyMetadata. Null on chips
   *  authored before this attr existed — see jumpTargetFromChipDom. */
  frameOfReferenceUid: string | null;
  /** Provenance: which agent produced this chip. parseHTML normalizes the
   * legacy "ai-default" value to "template" so old saved HTML still loads. */
  kind: FieldKind;
  /** AI confidence bucket for `ai-finding` chips. null otherwise. */
  confidence: FieldConfidence;
  /** JSON-encoded predicted attributes (cortex pipeline only). */
  candidates: string | null;
  /** JSON-encoded regulated Drafter [{dim,value}] selections. These values
   *  were gated upstream and intentionally carry no probability. */
  selectedAttributes: string | null;
  /** JSON-encoded engine-emitted phrasing {base, dims:[...]}. Present when the
   *  producing engine rendered the phrase (device-agnostic). The InlineChipCard
   *  pill toggle joins its pre-rendered option tokens — no client-side clinical
   *  logic — so a toggle can't drift from what the engine emitted. Null for
   *  pre-rollout studies and LLM-phrased devices (those use `candidates`). */
  phrasing: string | null;
  /** Original AI condition slug (e.g. "liver/steatosis"). Used by the
   *  InlineChipCard's pill toggle to rebuild the phrase from the canonical
   *  base name, not from the user-edited text. Without this the base
   *  name would drift on every toggle. */
  condition: string | null;
  /** JSON-encoded user-entered measurement {axesCm, volumeMl} written by the
   *  "Add measurement" path. Measurements aren't engine-emitted (no phrasing
   *  slot), so the chip carries them here: a pill toggle re-appends the suffix
   *  (and suppresses the size descriptor) instead of dropping it on re-render.
   *  Null until a measurement is taken; old chips parse to null (no migration). */
  measurement: string | null;
  /** The chip's text. Source of truth lives here, not in PM content. */
  text: string;
}

// A template-owned normal Impression is valid only while FINDINGS contains no
// authored or model observation. Enforce that as a document invariant instead
// of teaching every insertion surface (inline, strip, dictation, Add finding)
// to remember a second cleanup operation.
const templateImpressionInvariantKey = new PluginKey("templateImpressionInvariant");

function hasNonTemplateFinding(doc: ProseMirrorNode): boolean {
  let found = false;
  doc.descendants((node) => {
    if (found) return false;
    if (node.type.name !== "organSubsection") return true;
    node.descendants((child) => {
      if (found) return false;
      if (child.type.name !== "field") return true;
      const text = ((child.attrs.text as string | null) ?? "").trim();
      if ((child.attrs.kind as FieldKind) !== "template" && text) found = true;
      return false;
    });
    return false;
  });
  return found;
}

function templateImpressionActions(
  doc: ProseMirrorNode,
  removeNormalTemplate: boolean,
): {
  promote: number[];
  remove: Array<{ from: number; to: number }>;
} {
  const promote: number[] = [];
  const remove: Array<{ from: number; to: number }> = [];
  doc.descendants((node, pos) => {
    if (node.type.name === "impressionSection") return true;
    if (node.type.name !== "impressionItem") return false;
    if (node.attrs.provenance === "template") {
      let hasAuthoredField = false;
      node.descendants((child) => {
        if (child.type.name !== "field") return true;
        const text = ((child.attrs.text as string | null) ?? "").trim();
        if ((child.attrs.kind as FieldKind) !== "template" && text) hasAuthoredField = true;
        return false;
      });
      if (hasAuthoredField) {
        promote.push(pos);
      } else if (removeNormalTemplate) {
        remove.push({ from: pos, to: pos + node.nodeSize });
      }
    }
    return false;
  });
  return { promote, remove };
}

export const FieldNode = Node.create({
  name: "field",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      source: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-source"),
        renderHTML: (attrs) => (attrs.source ? { "data-source": attrs.source } : {}),
      },
      id: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-id"),
        renderHTML: (attrs) => (attrs.id ? { "data-id": attrs.id } : {}),
      },
      seriesId: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-series-id"),
        renderHTML: (attrs) => (attrs.seriesId ? { "data-series-id": attrs.seriesId } : {}),
      },
      sliceIndex: {
        default: null,
        parseHTML: (el) => {
          const val = el.getAttribute("data-slice-index");
          return val !== null ? Number.parseInt(val, 10) : null;
        },
        renderHTML: (attrs) =>
          attrs.sliceIndex !== null && attrs.sliceIndex !== undefined
            ? { "data-slice-index": String(attrs.sliceIndex) }
            : {},
      },
      frameOfReferenceUid: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-for-uid"),
        renderHTML: (attrs) =>
          attrs.frameOfReferenceUid ? { "data-for-uid": attrs.frameOfReferenceUid } : {},
      },
      kind: {
        default: null,
        parseHTML: (el) => {
          const v = el.getAttribute("data-kind");
          if (v === "ai-default") return "template";
          return v;
        },
        renderHTML: (attrs) => (attrs.kind ? { "data-kind": attrs.kind } : {}),
      },
      confidence: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-confidence"),
        renderHTML: (attrs) => (attrs.confidence ? { "data-confidence": attrs.confidence } : {}),
      },
      candidates: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-candidate-attrs"),
        renderHTML: (attrs) =>
          attrs.candidates ? { "data-candidate-attrs": attrs.candidates } : {},
      },
      selectedAttributes: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-selected-attributes"),
        renderHTML: (attrs) =>
          attrs.selectedAttributes ? { "data-selected-attributes": attrs.selectedAttributes } : {},
      },
      phrasing: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-phrasing"),
        renderHTML: (attrs) => (attrs.phrasing ? { "data-phrasing": attrs.phrasing } : {}),
      },
      condition: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-condition"),
        renderHTML: (attrs) => (attrs.condition ? { "data-condition": attrs.condition } : {}),
      },
      measurement: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-measurement"),
        renderHTML: (attrs) => (attrs.measurement ? { "data-measurement": attrs.measurement } : {}),
      },
      text: {
        default: "",
        // parseHTML for legacy buildReportDoc output (text as element content)
        // AND for current shape (text on data-text attribute, with content as
        // a copy-paste-friendly fallback). textContent wins when both exist
        // because copy/paste round-trips through HTML and only carries content.
        parseHTML: (el) => el.getAttribute("data-text") ?? el.textContent ?? "",
        renderHTML: (attrs) => (attrs.text ? { "data-text": attrs.text } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: "span.report-field" }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const hasLink =
      HTMLAttributes["data-series-id"] !== undefined &&
      HTMLAttributes["data-slice-index"] !== undefined;
    const className = hasLink ? "report-field has-link" : "report-field";
    return ["span", mergeAttributes(HTMLAttributes, { class: className }), node.attrs.text ?? ""];
  },

  // Tiptap pass-through to `schema.toText` — used by `editor.getText()`
  // and `getTextBetween` helpers. Without it, atoms contribute "" to text
  // walks and chip content disappears from dictation / getReportText.
  renderText({ node }) {
    return (node.attrs.text as string | null) ?? "";
  },

  // PM's native textBetween uses `leafText` on inline atoms (Cmd+F finds
  // chip text too). Tiptap doesn't have a top-level passthrough — go via
  // extendNodeSchema, filtered to only this node so it doesn't spread the
  // function onto every node in the schema.
  extendNodeSchema(extension) {
    if (extension.name !== "field") return {};
    return {
      leafText: (node: { attrs: Record<string, unknown> }) =>
        (node.attrs.text as string | null) ?? "",
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: templateImpressionInvariantKey,
        appendTransaction(transactions, _oldState, newState) {
          if (!transactions.some((transaction) => transaction.docChanged)) return null;
          const actions = templateImpressionActions(
            newState.doc,
            hasNonTemplateFinding(newState.doc),
          );
          if (actions.promote.length === 0 && actions.remove.length === 0) return null;
          const tr = newState.tr;
          for (const pos of actions.promote) tr.setNodeAttribute(pos, "provenance", "modified");
          actions.remove.sort((a, b) => b.from - a.from);
          for (const range of actions.remove) tr.delete(range.from, range.to);
          return tr;
        },
      }),
    ];
  },

  addStorage() {
    return {
      // Set by PrototypeReport when the editor mounts so ChipView's inline
      // ↗ icon and the InlineChipCard's "jump" button can call out to the
      // viewer. Read via `editor.storage.field.onJump`.
      onJump: null as ((target: FindingJumpTarget) => void) | null,
    };
  },

  addCommands() {
    const navigate =
      (dir: "next" | "prev") =>
      ({ editor, tr, dispatch }: CommandProps) => {
        const fields: Array<{
          id: string | null;
          start: number;
          end: number;
          seriesId: string | null;
          sliceIndex: number | null;
          frameOfReferenceUid: string | null;
        }> = [];
        editor.state.doc.descendants((node: ProseMirrorNode, pos: number) => {
          if (node.type.name !== "field") return true;
          fields.push({
            id: (node.attrs.id as string | null) ?? null,
            start: pos,
            end: pos + node.nodeSize,
            seriesId: (node.attrs.seriesId as string | null) ?? null,
            sliceIndex: (node.attrs.sliceIndex as number | null) ?? null,
            frameOfReferenceUid: (node.attrs.frameOfReferenceUid as string | null) ?? null,
          });
          return false;
        });
        if (fields.length === 0) return false;
        // Resolve "current chip" from the SSoT (reportFocusAtom). Cases:
        //   (a) chip-focused / caret-in-chip → chipId is canonical (DOM
        //       caret authority — PM selection lags behind clicks into a
        //       chip's inner contentEditable and using PM as the anchor
        //       gave the "skips one chip" bug).
        //   (b) caret-in-prose / none → fall back to PM selection.
        // These cases are disjoint by construction (single-writer per mode).
        const focusedChipId = getFocusedChipId(getDefaultStore().get(reportFocusAtom));
        const sel = editor.state.selection;
        let curr = -1;
        if (focusedChipId) {
          curr = fields.findIndex((f) => f.id === focusedChipId);
        } else {
          curr = fields.findIndex((f) => sel.from >= f.start && sel.from <= f.end);
        }
        let targetIdx: number;
        if (curr !== -1) {
          targetIdx =
            dir === "next"
              ? (curr + 1) % fields.length
              : (curr - 1 + fields.length) % fields.length;
        } else if (dir === "next") {
          const after = fields.findIndex((f) => f.start > sel.from);
          targetIdx = after === -1 ? 0 : after;
        } else {
          let last = -1;
          for (let i = fields.length - 1; i >= 0; i--) {
            if (fields[i].end < sel.from) {
              last = i;
              break;
            }
          }
          targetIdx = last === -1 ? fields.length - 1 : last;
        }
        const target = fields[targetIdx];
        if (!target) return false;
        if (dispatch) {
          // Set NodeSelection on the target chip so PM/Tiptap surfaces see
          // it as the active chip (focused-chip mode, getReportText, etc.).
          // Then focus the inner contentEditable so typing lands in the
          // chip — browser-native caret beats PM's selection for typing.
          tr.setSelection(NodeSelection.create(tr.doc, target.start));
          tr.scrollIntoView();
          // Defer DOM focus to after PM commits — PM tries to refocus its
          // root on dispatch, we want our inner-span focus to win. Guard
          // against editor teardown so a destroyed view doesn't crash the
          // rAF callback (test cleanup races).
          const targetStart = target.start;
          requestAnimationFrame(() => {
            if (editor.isDestroyed || !editor.view) return;
            const outer = editor.view.nodeDOM(targetStart) as HTMLElement | null;
            const inner = outer?.querySelector?.(`.${CHIP_INNER_CLASS}`) as HTMLElement | null;
            if (!inner) return;
            inner.focus();
            if (inner.firstChild) {
              const range = document.createRange();
              range.selectNodeContents(inner);
              const winSel = window.getSelection();
              winSel?.removeAllRanges();
              winSel?.addRange(range);
            }
          });
          // Write the SSoT before the dispatch's selectionUpdate listener
          // can fire — chip-focused mode wins over the caret-in-prose write
          // that would otherwise land first based on the NodeSelection
          // position. sectionKind is resolved against tr.doc (the
          // post-transaction doc) so future-mapped positions stay correct.
          if (target.id) {
            getDefaultStore().set(reportFocusAtom, {
              mode: "chip-focused",
              chipId: target.id,
              sectionKind: resolveSectionKindAt(tr.doc, target.start),
            });
          }
          // Fire the viewer's slice jump if this chip is linked.
          const onJump = editor.storage.field?.onJump;
          if (onJump && target.seriesId && target.sliceIndex != null) {
            onJump({
              seriesId: target.seriesId,
              sliceIndex: target.sliceIndex,
              frameOfReferenceUid: target.frameOfReferenceUid,
            });
          }
        }
        return true;
      };

    const replaceChipText =
      (chipId: string, text: string) =>
      ({ tr, dispatch }: CommandProps) => {
        const hit = findFieldNodeById(tr.doc, chipId);
        if (!hit) return false;
        if (!dispatch) return true;
        tr.setNodeAttribute(hit.pos, "text", text);
        // Promote stock template chips to "rad-added" on first overwrite
        // so section-delete's classifier treats the rewritten chip as
        // authored content. Mirrors ChipView.handleInput's promotion
        // (marks.ts is the SSoT for this rule).
        if ((hit.node.attrs.kind as string | null) === "template") {
          tr.setNodeAttribute(hit.pos, "kind", "rad-added");
        }
        return true;
      };

    const acceptIntoSection =
      ({ section, fieldAttrs }: { section: string; fieldAttrs: Partial<FieldNodeAttrs> }) =>
      ({ state, tr, dispatch }: CommandProps) => {
        const fieldType = state.schema.nodes.field;
        if (!fieldType) return false;

        let subStart = -1;
        let firstParaInner = -1;
        state.doc.descendants((node: ProseMirrorNode, pos: number) => {
          if (subStart !== -1) return false;
          if (node.type.name === "findingsSection") return true;
          if (node.type.name !== "organSubsection") return false;
          if ((node.attrs.kind as string) !== section) return false;
          subStart = pos;
          node.forEach((child: ProseMirrorNode, childOffset: number) => {
            if (firstParaInner !== -1 || child.type.name !== "paragraph") return;
            const paraStart = pos + 1 + childOffset;
            firstParaInner = paraStart + child.nodeSize - 1;
          });
          return false;
        });
        if (subStart === -1 || firstParaInner === -1) return false;

        if (!dispatch) return true;

        // Atomic field carrying the AI metadata. Text lives on attrs so the
        // ChipView NodeView can render the inner contenteditable; PM treats
        // the chip as one unit for navigation and deletion.
        const newField = fieldType.create({ ...fieldAttrs });

        // Single-template-chip state (fresh strip/off arms): the only chip
        // in the paragraph is the template default. Replace it so the
        // section ends up holding just the accepted AI chip — matches
        // staging's buildReportDoc behavior when AI is present.
        const paraStart = subStart + 1;
        const paraNode = state.doc.nodeAt(paraStart);
        if (paraNode && paraNode.type.name === "paragraph" && paraNode.childCount === 1) {
          const only = paraNode.firstChild;
          if (only && only.type.name === "field" && (only.attrs.kind as string) === "template") {
            const onlyStart = paraStart + 1;
            tr.replaceWith(onlyStart, onlyStart + only.nodeSize, newField);
            tr.scrollIntoView();
            return true;
          }
        }

        // Otherwise append at the end of the paragraph, separated by a space
        // if the previous content isn't already whitespace.
        const charBefore =
          firstParaInner > 0 ? tr.doc.textBetween(firstParaInner - 1, firstParaInner) : "";
        const needsSpace = !!charBefore && charBefore !== " " && charBefore !== "\n";
        if (needsSpace) tr.insert(firstParaInner, state.schema.text(" "));
        const insertAt = needsSpace ? firstParaInner + 1 : firstParaInner;
        tr.insert(insertAt, newField);
        tr.scrollIntoView();
        return true;
      };

    return {
      nextField: () => navigate("next"),
      prevField: () => navigate("prev"),
      acceptIntoSection,
      replaceChipText,
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ChipView);
  },

  addKeyboardShortcuts() {
    return {
      // Two-press delete: Backspace with the caret immediately after a field
      // SELECTS it (visible focus ring — "this is what you're about to
      // delete"); a second Backspace on the NodeSelection deletes. This is
      // PM's stock atom behavior, restored deliberately: the earlier
      // one-keystroke shortcut let a rad backspacing through adjacent prose
      // overshoot by one press and silently drop a whole finding from the
      // report (pilot feedback, 2026-06-23 sessions).
      //
      // Edit-mode gate: if DOM focus is in a chip's inner contentEditable,
      // the rad is typing into that chip — Backspace must delete a
      // character, not the whole chip. PM sees NodeSelection (set by Tab),
      // but the DOM caret authority overrides that for keystrokes.
      Backspace: ({ editor }) => {
        if (typeof document !== "undefined") {
          const active = document.activeElement;
          if (active instanceof Element && active.classList.contains(CHIP_INNER_CLASS)) {
            return false;
          }
        }
        const { state } = editor;
        const { selection } = state;
        if (selection instanceof NodeSelection && selection.node.type.name === "field") {
          return editor.commands.deleteSelection();
        }
        if (selection.empty) {
          const $from = selection.$from;
          const nodeBefore = $from.nodeBefore;
          if (nodeBefore && nodeBefore.type.name === "field") {
            const from = $from.pos - nodeBefore.nodeSize;
            return editor
              .chain()
              .command(({ tr }) => {
                tr.setSelection(NodeSelection.create(tr.doc, from));
                return true;
              })
              .run();
          }
        }
        return false;
      },
    };
  },
});
